import { useEffect, useRef, useState } from "react";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { Plus, Trash2, GripVertical, Save } from "lucide-react";
import { formatCalendarDate, formatCurrency } from "@/lib/format";
import { numberFieldProps } from "@/lib/number-field";
import { toast } from "sonner";
import { CreateCustomerDialog } from "@/components/CreateCustomerDialog";
import { CreateProductDialog } from "@/components/CreateProductDialog";
import { useInvoiceResources, useCurrencyScopedProducts, type TaxRateRow } from "@/hooks/useInvoiceResources";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { SearchCombobox, type SearchComboboxHit } from "@/components/SearchCombobox";
import { ErrorState, LoadingState } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { cn } from "@/lib/utils";
import {
  DiscountFields,
  computeTotals,
  discountPayload,
  discountValueIssue,
  discountExceedsIssues,
  discountTypeSchema,
  discountValueSchema,
  EarlyPaymentDiscountFields,
  earlyPaymentDiscountAmount,
  earlyPaymentDiscountIssues,
  earlyPaymentDiscountPayload,
} from "@/components/Discount";

type ProductResponse = components["schemas"]["ProductResponse"];


const createInvoiceSchema = z.object({
  customerId: z.string().min(1, "Customer is required"),
  currency: z.string().min(1, "Currency is required"),
  memo: z.string().nullable(),
  ccEmails: z.string().nullable(),
  paymentTermsDays: z
    .number()
    .int("Payment terms must be a whole number of days")
    .min(0, "Payment terms cannot be negative")
    .max(365, "Payment terms cannot exceed 365 days"),
  discountType: discountTypeSchema,
  discountValue: discountValueSchema,
  earlyPaymentDiscountType: discountTypeSchema,
  earlyPaymentDiscountValue: discountValueSchema,
  earlyPaymentDiscountExpiresOn: z.string().nullable(),
  lineItems: z
    .array(
      z.object({
        productId: z.string().min(1, "Product is required"),
        quantity: z.number().min(0.01, "Quantity must be greater than 0"),
        taxRateId: z.string().nullable(),
        discountType: discountTypeSchema,
        discountValue: discountValueSchema,
        // Display-only, not sent: lets the picker show a product outside the loaded page of results.
        productName: z.string().nullish(),
      }),
    )
    .min(1, "At least one line item is required"),
})
  .superRefine((values, ctx) => {
    const headerIssue = discountValueIssue(values.discountType, values.discountValue);
    if (headerIssue)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["discountValue"], message: headerIssue });

    values.lineItems.forEach((item, index) => {
      const issue = discountValueIssue(item.discountType, item.discountValue);
      if (issue)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lineItems", index, "discountValue"],
          message: issue,
        });
    });
  });

type CreateInvoiceFormValues = z.infer<typeof createInvoiceSchema>;

// One source for both useForm and the post-load reset: reset() replaces every value, so a field
// missing from either copy comes back undefined and fails validation.
const EMPTY_INVOICE: CreateInvoiceFormValues = {
  customerId: "",
  currency: "",
  memo: "",
  ccEmails: "",
  // Overwritten with the organisation's configured default once settings load (see fetchData).
  paymentTermsDays: 30,
  discountType: "None",
  discountValue: null,
  earlyPaymentDiscountType: "None",
  earlyPaymentDiscountValue: null,
  earlyPaymentDiscountExpiresOn: null,
  lineItems: [{ productId: "", quantity: 1, taxRateId: null, discountType: "None", discountValue: null, productName: null }],
};

function LineItemRow({
  index,
  control,
  products,
  taxRates,
  getProductPrice,
  currency,
  dragIndex,
  onDragStart,
  onDragOver,
  onDragEnd,
  onRemove,
  onNewProduct,
  searchProducts,
  onProductPicked,
}: {
  index: number;
  control: ReturnType<typeof useForm<CreateInvoiceFormValues>>["control"];
  products: ProductResponse[];
  taxRates: TaxRateRow[];
  getProductPrice: (productId: string) => number;
  currency: string;
  dragIndex: number | null;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onRemove: (index: number) => void;
  onNewProduct: () => void;
  searchProducts: (query: string, currency: string) => Promise<SearchComboboxHit[]>;
  onProductPicked: (index: number, hit: SearchComboboxHit) => void;
}) {
  const selectedProductId = useWatch({ control, name: `lineItems.${index}.productId` });
  const productName = useWatch({ control, name: `lineItems.${index}.productName` });
  const quantity = useWatch({ control, name: `lineItems.${index}.quantity` });
  const discountType = useWatch({ control, name: `lineItems.${index}.discountType` });
  const discountValue = useWatch({ control, name: `lineItems.${index}.discountValue` });
  const unitPrice = getProductPrice(selectedProductId);
  // The invoice-level discount is apportioned across every line, so it shows in the totals, not per row.
  const linePreview = computeTotals([
    { quantity: quantity || 0, unitPrice, discountType, discountValue, taxRatePercent: 0 },
  ]);
  const lineGross = linePreview.subTotal;
  const lineDiscount = linePreview.lineDiscountTotal;
  const lineTotal = lineGross - lineDiscount;

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      className={cn(
        "space-y-3 rounded-lg border bg-card/40 p-4 transition-opacity",
        dragIndex === index && "opacity-50",
      )}
    >
      <div className="flex items-center gap-2">
        <GripVertical className="size-4 text-muted-foreground cursor-grab shrink-0" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Line {index + 1}
        </span>
        <div className="flex-1" />
        {lineGross > 0 && (
          <span className="text-sm font-semibold tabular-nums">
            {lineDiscount > 0 && (
              <span className="mr-2 font-normal text-muted-foreground line-through">
                {formatCurrency(lineGross, currency)}
              </span>
            )}
            {formatCurrency(lineTotal, currency)}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          aria-label={`Remove line ${index + 1}`}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,0.9fr)]">
        <FormField
          control={control}
          name={`lineItems.${index}.productId`}
          render={({ field: itemField }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Product</FormLabel>
              <FormControl>
                <SearchCombobox
                  value={itemField.value}
                  testId="invoice-product-combobox"
                  selectedLabel={
                    products.find((p) => p.id === itemField.value)?.name ??
                    productName ??
                    undefined
                  }
                  placeholder="Select product"
                  searchPlaceholder="Search products…"
                  emptyText="No products found"
                  search={(q) => searchProducts(q, currency)}
                  fetchOnOpen
                  onSelect={(id, hit) => {
                    itemField.onChange(id);
                    onProductPicked(index, hit);
                  }}
                  createOption={{ label: "New Product", onSelect: onNewProduct }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lineItems.${index}.taxRateId`}
          render={({ field: itemField }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Tax Rate</FormLabel>
              <Select
                onValueChange={(val) => itemField.onChange(val === "__none__" ? null : val)}
                value={itemField.value ?? "__none__"}
              >
                <FormControl>
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {taxRates.map((rate) => (
                    <SelectItem key={rate.id} value={rate.id}>
                      <span>
                        {rate.name} ({rate.rate}%)
                      </span>
                      {rate.source === "country" && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          Country rate
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`lineItems.${index}.quantity`}
          render={({ field: itemField }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-xs">Quantity</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  min="0.01"
                  {...itemField}
                  {...numberFieldProps(itemField)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <DiscountFields
        control={control}
        typeName={`lineItems.${index}.discountType`}
        valueName={`lineItems.${index}.discountValue`}
        currency={currency}
        label="Line discount"
        compact
      />

      {selectedProductId && (
        <p className="text-xs text-muted-foreground">
          Unit price: {formatCurrency(unitPrice, currency)}
          {lineDiscount > 0 && `, less ${formatCurrency(lineDiscount, currency)} discount`}
        </p>
      )}
    </div>
  );
}

/** Mirrors the server's totals formula; the saved invoice's figures always come from the API. */
function InvoiceTotalsCard({
  control,
  getProductPrice,
  taxRates,
  children,
}: {
  control: ReturnType<typeof useForm<CreateInvoiceFormValues>>["control"];
  getProductPrice: (productId: string) => number;
  taxRates: TaxRateRow[];
  children?: React.ReactNode;
}) {
  const currency = useWatch({ control, name: "currency" });
  const lineItems = useWatch({ control, name: "lineItems" });
  const discountType = useWatch({ control, name: "discountType" });
  const discountValue = useWatch({ control, name: "discountValue" });
  const paymentTermsDays = useWatch({ control, name: "paymentTermsDays" });
  const earlyPaymentType = useWatch({ control, name: "earlyPaymentDiscountType" });
  const earlyPaymentValue = useWatch({ control, name: "earlyPaymentDiscountValue" });
  const earlyPaymentExpiresOn = useWatch({ control, name: "earlyPaymentDiscountExpiresOn" });

  const totals = computeTotals(
    (lineItems ?? []).map((item) => ({
      quantity: item?.quantity || 0,
      unitPrice: getProductPrice(item?.productId ?? ""),
      discountType: item?.discountType,
      discountValue: item?.discountValue,
      taxRatePercent: Number(taxRates.find((r) => r.id === item?.taxRateId)?.rate ?? 0),
    })),
    discountType,
    discountValue,
  );

  // A line without a product prices at zero, so the total would quietly understate the invoice.
  const unpriced = (lineItems ?? []).filter((item) => !item?.productId).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <DiscountFields
          control={control}
          typeName="discountType"
          valueName="discountValue"
          currency={currency}
          label="Invoice discount"
        />
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Applied after line discounts, before tax.
          <InfoHint label="More about the invoice discount">
            The discount is spread across the lines before tax, so tax is charged on the discounted
            amount.
          </InfoHint>
        </p>

        <dl className="space-y-1.5 border-t pt-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="tabular-nums">{formatCurrency(totals.subTotal, currency)}</dd>
          </div>
          {totals.discountTotal > 0 && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Discount</dt>
              <dd className="tabular-nums">-{formatCurrency(totals.discountTotal, currency)}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Tax</dt>
            <dd className="tabular-nums">{formatCurrency(totals.taxAmount, currency)}</dd>
          </div>
          <div className="flex items-baseline justify-between border-t pt-2">
            <dt className="font-semibold">Total</dt>
            <dd className="text-xl font-bold tracking-tight tabular-nums">
              {formatCurrency(totals.totalAmount, currency)}
            </dd>
          </div>
          <EarlyPaymentPreview
            totalAmount={totals.totalAmount}
            currency={currency}
            type={earlyPaymentType}
            value={earlyPaymentValue}
            expiresOn={earlyPaymentExpiresOn}
          />
        </dl>
        <div className="border-t pt-3">
          <EarlyPaymentDiscountFields
            control={control}
            typeName="earlyPaymentDiscountType"
            valueName="earlyPaymentDiscountValue"
            expiresOnName="earlyPaymentDiscountExpiresOn"
            currency={currency}
            paymentTermsDays={paymentTermsDays}
          />
        </div>
        {unpriced > 0 ? (
          <p className="text-xs text-amber-600">
            {unpriced === 1 ? "1 line has" : `${unpriced} lines have`} no product yet, so the total is
            incomplete.
          </p>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

/** "£1,176.00 if paid by 5 Nov 2026" under the total. Shared with the edit page. */
export function EarlyPaymentPreview({
  totalAmount,
  currency,
  type,
  value,
  expiresOn,
}: {
  totalAmount: number;
  currency: string;
  type: "None" | "Percentage" | "Fixed" | null | undefined;
  value: number | null | undefined;
  expiresOn: string | null | undefined;
}) {
  const amount = earlyPaymentDiscountAmount(type, value, totalAmount);
  if (amount <= 0 || amount >= totalAmount || !expiresOn) return null;
  return (
    <div className="flex justify-between text-sm" data-testid="early-payment-preview">
      <dt className="text-muted-foreground">If paid by {formatCalendarDate(expiresOn)}</dt>
      <dd className="font-medium tabular-nums">{formatCurrency(totalAmount - amount, currency)}</dd>
    </div>
  );
}

export interface CreateInvoicePageProps {
  onCreated: (invoiceId: string) => void;
  onCancel: () => void;
}

export function CreateInvoicePage({ onCreated, onCancel }: CreateInvoicePageProps) {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const resources = useInvoiceResources();
  const { customers, products, currencies, taxRates, getProductPrice, upsertProducts } = resources;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  const form = useForm<CreateInvoiceFormValues>({
    resolver: zodResolver(createInvoiceSchema),
    defaultValues: EMPTY_INVOICE,
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "lineItems",
  });

  useCurrencyScopedProducts(form);

  async function fetchOrgDefaultPaymentTermsDays(): Promise<number> {
    if (!organisationId) return 30;
    const { data } = await api.GET("/v1/organisations/{organisationId}/settings/ar", {
      params: { path: { organisationId } },
    });
    const configured = data?.defaultPaymentTermsDays;
    return configured != null ? Number(configured) : 30;
  }

  // SearchCombobox debounces these.
  async function searchCustomers(query: string): Promise<SearchComboboxHit[]> {
    const { data } = await api.GET("/v1/customers", {
      params: { query: { Name: query || undefined, Limit: 10 } },
    });
    return (data?.data ?? []).map((c) => ({
      id: c.id!,
      name: c.name,
      sublabel: c.email,
    }));
  }

  async function searchProducts(query: string, currency: string): Promise<SearchComboboxHit[]> {
    const { data } = await api.GET("/v1/products", {
      params: { query: { Name: query || undefined, Currency: currency || undefined, Limit: 10 } },
    });
    const items = data?.data ?? [];
    // Keep picked products resolvable by getProductPrice even if they weren't in the initial page.
    upsertProducts(items);
    return items.map((p) => ({
      id: p.id!,
      name: p.name,
      sublabel: p.description,
    }));
  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const loaded = await resources.fetchAll({ includeLists: false });
        const orgDefaultTerms = await fetchOrgDefaultPaymentTermsDays();
        form.reset({
          ...EMPTY_INVOICE,
          currency: loaded.defaultCurrency ?? "",
          paymentTermsDays: orgDefaultTerms,
        });
      } catch (e) {
        console.error("Failed to load data:", e);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  async function onSubmit(values: CreateInvoiceFormValues) {
    // Discount-exceeds checks need resolved prices, so they can't live in the zod schema; the API would 422.
    const totals = computeTotals(
        values.lineItems.map((item) => ({
          quantity: item.quantity || 0,
          unitPrice: getProductPrice(item.productId),
          discountType: item.discountType,
          discountValue: item.discountValue,
          taxRatePercent: Number(taxRates.find((r) => r.id === item.taxRateId)?.rate ?? 0),
        })),
        values.discountType,
        values.discountValue,
      );
    const exceeded = discountExceedsIssues(totals);
    const firstLineIssue = exceeded.lines.findIndex((issue) => issue !== null);
    if (firstLineIssue !== -1) {
      form.setError(`lineItems.${firstLineIssue}.discountValue`, {
        message: exceeded.lines[firstLineIssue] ?? undefined,
      });
      return;
    }
    if (exceeded.invoice) {
      form.setError("discountValue", { message: exceeded.invoice });
      return;
    }
    const earlyPaymentIssues = earlyPaymentDiscountIssues(
      values.earlyPaymentDiscountType,
      values.earlyPaymentDiscountValue,
      values.earlyPaymentDiscountExpiresOn,
      totals.totalAmount,
      values.paymentTermsDays,
    );
    if (earlyPaymentIssues.value || earlyPaymentIssues.expiresOn) {
      if (earlyPaymentIssues.value) form.setError("earlyPaymentDiscountValue", { message: earlyPaymentIssues.value });
      if (earlyPaymentIssues.expiresOn)
        form.setError("earlyPaymentDiscountExpiresOn", { message: earlyPaymentIssues.expiresOn });
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const { data, error: apiError } = await api.POST("/v1/invoices", {
        body: {
          customerId: values.customerId,
          currency: values.currency,
          memo: values.memo || null,
          // Blank leaves ccEmails undefined so the invoice inherits the organisation default.
          ccEmails: values.ccEmails && values.ccEmails.trim()
            ? values.ccEmails.split(/[,\n]/).map((e) => e.trim()).filter((e) => e.length > 0)
            : undefined,
          paymentTermsDays: values.paymentTermsDays,
          ...discountPayload(values.discountType, values.discountValue),
          earlyPaymentDiscount: earlyPaymentDiscountPayload(
            values.earlyPaymentDiscountType,
            values.earlyPaymentDiscountValue,
            values.earlyPaymentDiscountExpiresOn,
          ),
          lineItems: values.lineItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity,
            taxRateId: item.taxRateId || null,
            ...discountPayload(item.discountType, item.discountValue),
          })),
        },
      });

      if (apiError || !data?.id) {
        const message = apiErrorMessage(apiError, "Failed to create invoice.");
        setSaveError(message);
        toast.error(message);
        return;
      }

      onCreated(data.id);
    } catch (e) {
      console.error("Failed to create invoice:", e);
      setSaveError("An unexpected error occurred while creating the invoice.");
    } finally {
      setSaving(false);
    }
  }

  function handleDragStart(index: number) {
    dragIndexRef.current = index;
    setDragIndex(index);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    if (dragIndexRef.current === null || dragIndexRef.current === index) return;
    move(dragIndexRef.current, index);
    dragIndexRef.current = index;
  }

  function handleDragEnd() {
    dragIndexRef.current = null;
    setDragIndex(null);
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Getting the form ready..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <BackLink onClick={onCancel}>Back to invoices</BackLink>
        <ErrorState message="Couldn't load your customers, products and tax rates." onRetry={() => window.location.reload()} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink onClick={onCancel}>Back to invoices</BackLink>
      <PageHeader
        eyebrow="Receivables"
        title="New invoice"
        subtitle="Saved as a draft. Nothing reaches the customer until you send it."
      />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid items-start gap-6 @4xl:grid-cols-[minmax(0,1fr)_20rem]"
        >
          <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Who and when</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2">
                <FormField
                  control={form.control}
                  name="customerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer</FormLabel>
                      <FormControl>
                        <SearchCombobox
                          value={field.value}
                          testId="invoice-customer-combobox"
                          selectedLabel={
                            customers.find((c) => c.id === field.value)?.name ?? undefined
                          }
                          placeholder="Select customer"
                          searchPlaceholder="Search customers…"
                          emptyText="No customers found"
                          search={searchCustomers}
                          fetchOnOpen
                          onSelect={(id) => field.onChange(id)}
                          createOption={{
                            label: "New Customer",
                            onSelect: () => resources.setNewCustomerOpen(true),
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {currencies.map((c) => (
                            <SelectItem key={c.id} value={c.code}>
                              {c.code} - {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="memo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Memo</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="A note printed on the invoice"
                        className="resize-none"
                        rows={2}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ccEmails"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-1.5">
                      <FormLabel>CC (optional)</FormLabel>
                      <InfoHint label="More about CC">
                        Comma-separated. Leave blank to use your organisation's default CC list.
                      </InfoHint>
                    </div>
                    <FormControl>
                      <Input
                        placeholder="accounts@example.com, bookkeeper@example.com"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="paymentTermsDays"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-1.5">
                      <FormLabel>Payment terms (days)</FormLabel>
                      <InfoHint label="More about payment terms">
                        Days until the invoice is due. Starts from your organisation's default; 0 means
                        due on receipt.
                      </InfoHint>
                    </div>
                    <FormControl>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="365"
                        {...field}
                        {...numberFieldProps(field)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Lines</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  append({ productId: "", quantity: 1, taxRateId: null, discountType: "None" as const, discountValue: null, productName: null })
                }
              >
                <Plus className="size-4" />
                Add line
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {fields.length === 0 && (
                <div className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
                  No lines yet. Add one to price the invoice.
                </div>
              )}

              {fields.map((field, index) => (
                <LineItemRow
                  key={field.id}
                  index={index}
                  control={form.control}
                  products={products}
                  taxRates={taxRates}
                  getProductPrice={getProductPrice}
                  currency={form.getValues("currency")}
                  dragIndex={dragIndex}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onRemove={remove}
                  onNewProduct={() => resources.setNewProductOpen(true)}
                  searchProducts={searchProducts}
                  onProductPicked={(i, hit) =>
                    form.setValue(`lineItems.${i}.productName`, hit.name)
                  }
                />
              ))}

              {form.formState.errors.lineItems?.root && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.lineItems.root.message}
                </p>
              )}
            </CardContent>
          </Card>

          </div>

          <aside className="space-y-4 @4xl:sticky @4xl:top-6">
            <InvoiceTotalsCard
              control={form.control}
              getProductPrice={getProductPrice}
              taxRates={taxRates}
            >
              {saveError && (
                <p className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  {saveError}
                </p>
              )}
              <div className="flex flex-col gap-2 border-t pt-4">
                <Button type="submit" disabled={saving} className="w-full">
                  {saving ? <Spinner className="size-4" /> : <Save className="size-4" />}
                  {saving ? "Creating..." : "Create Invoice"}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </InvoiceTotalsCard>
          </aside>
        </form>
      </Form>

      <CreateCustomerDialog
        open={resources.newCustomerOpen}
        onOpenChange={resources.setNewCustomerOpen}
        onCreated={resources.refreshCustomers}
      />
      <CreateProductDialog
        open={resources.newProductOpen}
        onOpenChange={resources.setNewProductOpen}
        defaultCurrency={form.getValues("currency") || undefined}
        onCreated={() => resources.refreshProducts(form.getValues("currency"))}
      />
    </PageContainer>
  );
}
